import { useLocation, useNavigate } from "react-router";
import { getMoreMenuData } from "./menus";
import { useVisitorInfo } from "@/common/hooks";
import { useCallback, useMemo } from "react";
import { MoreMenu } from "./MoreMenu";
import { metricsBus } from "@/common/buses";
import {
  onCloseMoreMenu,
  onOpenAddCollectionPopup,
  onOpenEditGifNichesPopup,
  onOpenEditGifPreferencesPopup,
  onOpenReportPopup,
  onOpenShareGifMenu,
  onOpenEditGifPopup,
  onOpenAddNichePopup,
} from "@/components/popup-dispatcher/PopupDispatcher";
import { apiMain, apiNiches } from "@/common/services";
import { IMoreMenuContainerProps, ScrollSectionEnum, Roles } from "@/common/buses/popups/popup-types";
import { validateGifSubmission } from "@/common/utils/nicheUtils";
import { useFetch } from "@/common/hooks/useFetch";
import { getFeedback } from "@/common/utils/voteHelpers";

export const MoreMenuContainer: React.FC<IMoreMenuContainerProps> = ({
  gif,
  position,
  playCount,
  anchorEl,
  hideGif,
  onOpenTagFeedbackMode,
  addNicheFeedback,
  nicheFeedbacks,
}) => {
  const currentUser = useVisitorInfo();
  const location = useLocation();
  const navigate = useNavigate();

  const fetchMyNiches = useCallback(() => apiNiches.getMyNiches(), []);
  const { data: myNichesData } = useFetch(fetchMyNiches);

  const isNicheContentModerator = useMemo(() => {
    const pathNameSplit = location.pathname.split("/");
    const nicheId = pathNameSplit.pop();
    const pageName = pathNameSplit.pop();
    let isModerator = false;

    if (pageName !== "niches") {
      return isModerator;
    }

    myNichesData?.niches.forEach(({ id }) => {
      if (id === nicheId) {
        isModerator = true;
      }
    });

    return isModerator;
  }, [location, myNichesData]);

  const handleClose = useCallback(() => {
    onCloseMoreMenu();
  }, []);

  const handleHideGif = useCallback(async () => {
    try {
      await apiMain.hideGif({
        id: gif.id,
        home: true,
        trending: true,
      });

      hideGif(gif.id);
      console.debug(`Gif ${gif.id} hidden successfully.`);
    } catch (e) {
      console.error(`Failed to hide gif ${gif.id}.`);
    }

    handleClose();
  }, [gif.id, handleClose, hideGif]);

  const handleManageClick = useCallback(() => {
    handleClose();
    navigate(`/profile/manage?gifId=${gif.id}`);
  }, [navigate, gif.id, handleClose]);

  const handleOpenEditNichesDialog = useCallback((role: Roles) => {
    onOpenEditGifNichesPopup({ gifId: gif.id, role });
    handleClose();
  }, [gif.id, handleClose]);

  const toggleCollection = useCallback(() => {
    onOpenAddCollectionPopup({ gifId: gif.id, boosted: !!gif.cta, position, playCount });
    handleClose();
  }, [gif, handleClose, position, playCount]);

  const toggleNiche = useCallback(() => {
    if (validateGifSubmission(currentUser?.verified, currentUser?.creationtime)){
      onOpenAddNichePopup({ gifId: gif.id, tags: gif.tags, preferences: gif.sexuality });
    }
    handleClose();
  }, [gif.id, handleClose, currentUser]);

  const toggleReport = useCallback(() => {
    onOpenReportPopup({ gifId: gif.id });
    handleClose();
  }, [gif.id, handleClose]);

  const handleOpenEditGif = useCallback(() => {
    onOpenEditGifPopup({ gif, scrollToSection: ScrollSectionEnum.MANAGE_LINKS });
    handleClose();
  }, [gif, handleClose]);

  const handleOpenTagFeedback = () => {
    onOpenTagFeedbackMode?.();
    handleClose();
    metricsBus.emit("tagFeedbackEnabledOptions");
  };

  const toggleShareMenu = useCallback(() => {
    metricsBus.emit("shareClick");

    onOpenShareGifMenu({
      gif: gif,
      position,
      playCount,
      anchorEl: anchorEl,
    });

    handleClose();
  }, [anchorEl, gif, handleClose, position, playCount]);

  const toggleGifPrefs = useCallback(() => {
    onOpenEditGifPreferencesPopup({
      gifId: gif.id,
      sexuality: gif.sexuality || []
    });
    handleClose();
  }, [handleClose, gif]);

  const gifFeedback = useMemo(
    () => nicheFeedbacks ? getFeedback({ gif: gif.id, states: nicheFeedbacks }) : undefined,
    [nicheFeedbacks, gif.id]
  );

  const shouldDisplayVoteButtons = !!(!gif.cta && addNicheFeedback && gifFeedback);

  const handleRelevantVote = useCallback(() => {
    if (addNicheFeedback) {
      addNicheFeedback({ gif: gif.id, state: "up" });
    }
    handleClose();
  }, [addNicheFeedback, gif.id, handleClose]);

  const handleIrrelevantVote = useCallback(() => {
    if (addNicheFeedback) {
      addNicheFeedback({ gif: gif.id, state: "down" });
    }
    handleClose();
  }, [addNicheFeedback, gif.id, handleClose]);

  const actions = {
    handleHideGif,
    handleManageClick,
    handleOpenEditNichesDialog,
    handleOpenEditGif,
    handleOpenTagFeedback,
    toggleCollection,
    toggleNiche,
    toggleReport,
    toggleShareMenu,
    toggleGifPrefs,
    handleRelevantVote,
    handleIrrelevantVote,
  };

  const moreMenuData = getMoreMenuData({
    actions,
    currentUser,
    gif: gif,
    isNicheContentModerator,
    isFeedbackEnabled: !!onOpenTagFeedbackMode,
    shouldDisplayVoteButtons,
    gifFeedback,
  });

  return (
    <MoreMenu
      isOpen
      anchorEl={anchorEl}
      onClose={handleClose}
      menuItems={moreMenuData}
    />
  );
};
